import { Segmented, Tooltip } from 'antd';
import {
  COST_BY_CROP_NO_AREA_TOOLTIP,
  COST_BY_CROP_PLAN_VS_ACTUAL_NOTE,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_LABELS,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_PLAN_VS_ACTUAL_SECTION_ID,
} from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import React, { useMemo, useState } from 'react';
import { formatCurrency } from 'utils/CurrencyUtils';
import type { SummaryEntityCategory } from '../../../../../redux/slices/InsightsSlice';
import {
  type CostByCropRow,
  type CostByCropSideBySideRow,
  type CostByCropValues,
  resolveCostByCropValues,
} from './computeCostByCrop';
import type { SpendMetric } from './computePlanVsActual';
import CopyButton from './CopyButton';
import type { SummaryDataSide } from './dataBasis';
import { formatNumber } from './formatNumber';
import SectionCard from './SectionCard';
import { buildTsv } from './summary2Utils';

/** Fixed display order — matches the validated chart-1..6 categorical palette (never cycled/reordered per row). */
const CATEGORY_ORDER: SummaryEntityCategory[] = [
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
];

const CATEGORY_SWATCH: Record<SummaryEntityCategory, string> = {
  [SUMMARY_ENTITY_SEED]: 'bg-chart-1',
  [SUMMARY_ENTITY_SOIL_INPUT]: 'bg-chart-2',
  [SUMMARY_ENTITY_PLANT_PROTECTION]: 'bg-chart-3',
  [SUMMARY_ENTITY_BIOSTIMULANT]: 'bg-chart-4',
  [SUMMARY_ENTITY_OPERATION]: 'bg-chart-5',
} as Record<SummaryEntityCategory, string>;

interface CostByCropTableProps {
  rows: CostByCropSideBySideRow[];
  /** Plan or Actual shows the spend-mix table and bars; Plan vs Actual shows one labelled bar per side per crop and points to Plan vs Actual Spend for the dollar comparison (OP-2433). */
  sides: SummaryDataSide[];
}

interface CostByCropTotals extends CostByCropValues {
  areaHa: number;
}

const computeTotals = (
  rows: (CostByCropRow | undefined)[]
): CostByCropTotals => {
  const costs: Partial<Record<SummaryEntityCategory, number>> = {};
  let totalCost = 0;
  let areaHa = 0;
  for (const row of rows) {
    if (!row) continue;
    areaHa += row.areaHa;
    totalCost += row.totalCost;
    for (const category of CATEGORY_ORDER) {
      costs[category] = (costs[category] ?? 0) + (row.costs[category] ?? 0);
    }
  }
  return { costs, totalCost, areaHa };
};

const formatValue = (value: number, metric: SpendMetric): string =>
  `${formatCurrency(value)}${metric === 'perHa' ? '/ha' : ''}`;

const NoAreaCell: React.FC = () => (
  <Tooltip title={COST_BY_CROP_NO_AREA_TOOLTIP}>
    <span className="cursor-help font-normal text-gray-muted">—</span>
  </Tooltip>
);

/** Segments narrower than this (% of the bar) don't have room for a legible label. */
const LABEL_MIN_PCT = 6;

/**
 * One 100%-stacked segment bar per crop and side. Plain HTML/flex rather
 * than SVG — a non-uniformly-scaled SVG viewBox (needed to fill an arbitrary
 * container width) would distort the % labels' text; flex segments don't
 * have that problem. Segment width is a continuously-variable per-row value
 * with no fixed set of possibilities, so — same as this codebase's existing
 * `.workspace-progress__indicator` dynamic-width bar — it's set via the
 * `style` prop rather than a Tailwind class. The mix is the same in $ and
 * $/ha, so only the tooltip values follow the metric toggle.
 */
const CropCostBar: React.FC<{
  row: CostByCropRow;
  metric: SpendMetric;
  sideLabel?: string;
}> = ({ row, metric, sideLabel }) => {
  const scaled = resolveCostByCropValues(row, metric);
  return (
    <div className="flex items-center gap-3">
      <span
        className="w-40 shrink-0 truncate text-sm text-charcoal"
        title={row.cropLabel}
      >
        {row.cropLabel}
      </span>
      {sideLabel && (
        <span className="w-12 shrink-0 text-xs font-semibold text-gray-dark">
          {sideLabel}
        </span>
      )}
      <div className="flex h-10 w-full flex-1 overflow-hidden rounded-sm bg-gray-smoke">
        {CATEGORY_ORDER.map((category) => {
          const value = row.costs[category] ?? 0;
          const pct = row.totalCost > 0 ? (value / row.totalCost) * 100 : 0;
          if (pct <= 0) return null;
          const shown = scaled?.costs[category];
          return (
            <Tooltip
              key={category}
              title={`${SUMMARY_ENTITY_LABELS[category]}: ${shown === undefined ? COST_BY_CROP_NO_AREA_TOOLTIP : formatValue(shown, metric)} (${pct.toFixed(0)}%)`}
            >
              <div
                style={{ width: `${pct}%` }}
                className={`flex cursor-help items-center justify-center transition-[filter] hover:brightness-90 ${CATEGORY_SWATCH[category]}`}
              >
                {pct >= LABEL_MIN_PCT && (
                  <span className="text-base font-black text-charcoal">
                    {pct.toFixed(0)}%
                  </span>
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const CostByCropTable: React.FC<CostByCropTableProps> = ({ rows, sides }) => {
  const isPaired = sides.length > 1;
  const [metric, setMetric] = useState<SpendMetric>('total');
  const unitSuffix = metric === 'total' ? '($)' : '($/ha)';

  const totalsBySide = useMemo(
    () =>
      sides.map((side) => computeTotals(rows.map((row) => row.sides[side]))),
    [rows, sides]
  );

  // The copied TSV keeps the full breakdown in every data basis — including
  // Plan vs Actual, where the on-screen table is replaced by bars.
  const { copied, copy } = useCopyToClipboard(() => {
    const withSide = (header: string) =>
      isPaired
        ? sides.map((side) => `${header} (${SUMMARY_DATA_BASIS_LABELS[side]})`)
        : [header];
    const headers = [
      'Crop',
      ...withSide('Ha'),
      ...CATEGORY_ORDER.flatMap((c) =>
        withSide(`${SUMMARY_ENTITY_LABELS[c]} ${unitSuffix}`)
      ),
      ...withSide(`Total ${unitSuffix}`),
    ];
    const toCells = (
      sources: ((CostByCropValues & { areaHa: number }) | undefined)[]
    ) => {
      const scaled = sources.map((r) =>
        r ? resolveCostByCropValues(r, metric) : null
      );
      return [
        ...sources.map((r) => formatNumber(r?.areaHa ?? 0)),
        ...CATEGORY_ORDER.flatMap((c) =>
          scaled.map((v) => (v ? formatNumber(v.costs[c] ?? 0) : ''))
        ),
        ...scaled.map((v) => (v ? formatNumber(v.totalCost) : '')),
      ];
    };
    const body = rows.map((row) => [
      row.cropLabel,
      ...toCells(sides.map((side) => row.sides[side])),
    ]);
    body.push(['Total', ...toCells(totalsBySide)]);
    return buildTsv(headers, body);
  });

  if (rows.length === 0) return null;

  const handleJumpToPlanVsActual = () =>
    document
      .getElementById(SUMMARY_PLAN_VS_ACTUAL_SECTION_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const side = sides[0];
  const totals = totalsBySide[0];
  const scaledTotals = resolveCostByCropValues(totals, metric);

  const valueHeaders: { key: string; label: string; className: string }[] = [
    ...CATEGORY_ORDER.map((category) => ({
      key: category,
      label: `${SUMMARY_ENTITY_LABELS[category]} ${unitSuffix}`,
      className: 'w-32',
    })),
    { key: 'total', label: `Total ${unitSuffix}`, className: 'w-32' },
  ];

  return (
    <SectionCard
      id="summary2-costByCrop"
      title="Cost by Crop"
      actions={
        <>
          <Segmented<SpendMetric>
            aria-label="Cost by Crop metric"
            value={metric}
            onChange={setMetric}
            options={[
              { label: 'Total', value: 'total' },
              { label: '$/ha', value: 'perHa' },
            ]}
          />
          <CopyButton copied={copied} onCopy={copy} />
        </>
      }
    >
      {isPaired ? (
        <p className="text-sm text-gray-dark">
          {COST_BY_CROP_PLAN_VS_ACTUAL_NOTE}{' '}
          <button
            type="button"
            onClick={handleJumpToPlanVsActual}
            className="cursor-pointer border-none bg-transparent p-0 font-semibold text-primary underline hover:text-primary-dark"
          >
            Plan vs Actual Spend
          </button>
          .
        </p>
      ) : (
        <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
          <table className="w-max border-collapse text-base">
            <thead>
              <tr>
                <th className="max-w-[12rem] pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal">
                  Crop
                </th>
                <th className="w-20 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                  Ha
                </th>
                {valueHeaders.map((header) => (
                  <th
                    key={header.key}
                    className={`${header.className} px-2 pb-2 text-right text-sm font-bold text-charcoal`}
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const sideRow = row.sides[side];
                const scaled = sideRow
                  ? resolveCostByCropValues(sideRow, metric)
                  : null;
                return (
                  <tr
                    key={row.key}
                    className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="max-w-[12rem] py-1.5 pl-2 pr-6 text-base text-charcoal">
                      {row.cropLabel}
                    </td>
                    <td className="w-20 px-2 py-1.5 text-right tabular-nums text-charcoal">
                      {formatNumber(sideRow?.areaHa ?? 0)}
                    </td>
                    {CATEGORY_ORDER.map((category) => (
                      <td
                        key={category}
                        className="w-32 px-2 py-1.5 text-right tabular-nums text-charcoal"
                      >
                        {scaled ? (
                          formatCurrency(scaled.costs[category] ?? 0)
                        ) : (
                          <NoAreaCell />
                        )}
                      </td>
                    ))}
                    <td className="w-32 px-2 py-1.5 text-right font-semibold tabular-nums text-charcoal">
                      {scaled ? formatCurrency(scaled.totalCost) : <NoAreaCell />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="py-1.5 pl-2 pr-6 text-right text-base font-bold text-charcoal">
                  Total:
                </td>
                <td className="w-20 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {formatNumber(totals.areaHa)}
                </td>
                {CATEGORY_ORDER.map((category) => (
                  <td
                    key={category}
                    className="w-32 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal"
                  >
                    {scaledTotals ? (
                      formatCurrency(scaledTotals.costs[category] ?? 0)
                    ) : (
                      <NoAreaCell />
                    )}
                  </td>
                ))}
                <td className="w-32 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                  {scaledTotals ? (
                    formatCurrency(scaledTotals.totalCost)
                  ) : (
                    <NoAreaCell />
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div
        className={`${isPaired ? 'mt-3' : 'mt-6'} w-full max-w-3xl rounded-md border border-gray-bright p-4`}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {CATEGORY_ORDER.map((category) => (
            <span
              key={category}
              className="inline-flex items-center gap-1.5 text-sm text-gray-dark"
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm ${CATEGORY_SWATCH[category]}`}
              />
              {SUMMARY_ENTITY_LABELS[category]}
            </span>
          ))}
        </div>
        <div className="mt-3 flex flex-col gap-3">
          {rows.flatMap((row) =>
            sides.map((barSide) => {
              const sideRow = row.sides[barSide];
              // One bar per side — a side with no cost for this crop gets no bar.
              if (!sideRow) return null;
              return (
                <CropCostBar
                  key={`${row.key}-${barSide}`}
                  row={sideRow}
                  metric={metric}
                  sideLabel={
                    isPaired ? SUMMARY_DATA_BASIS_LABELS[barSide] : undefined
                  }
                />
              );
            })
          )}
        </div>
      </div>
    </SectionCard>
  );
};

export default CostByCropTable;
